// FEX Carrier Placement data — used by the FEX Hub's Placement tool.
// Source: the underwriting grid from the FEX Carrier Placement Tool
// (Eagle Premier, Accendo, New Vista, Senior Choice, Plan Right,
// Living Promise). The carrier order in every condition's `verdicts`
// array MUST match the FEX_CARRIERS order below — verdicts[ci] looks
// up by index, not by name.
//
// Tier rank is used to pick the "best fit" carrier — the carrier
// with the LOWEST worst-tier across the client's selected
// conditions wins. The carrier's e-application is always the final
// authority; this is a point-of-sale shortcut, not underwriting.

export const FEX_CARRIERS = [
  { name: 'Americo',          product: 'Eagle Premier' },
  { name: 'Aetna / CVS',      product: 'Accendo' },
  { name: 'Prosperity',       product: 'New Vista' },
  { name: 'American-Amicable', product: 'Senior Choice' },
  { name: 'Foresters',        product: 'Plan Right' },
  { name: 'Mutual of Omaha',  product: 'Living Promise' },
];

// Tiers ordered by goodness for the placement decision. Lower r =
// better outcome for the client. `tone` maps to the CRM's Tailwind
// color palette so badges blend with the rest of the hub.
export const FEX_TIERS = {
  ok:      { r: 1,   tone: 'emerald', label: 'Day-One' },
  graded:  { r: 2,   tone: 'amber',   label: 'Graded' },
  check:   { r: 2.5, tone: 'slate',   label: 'Verify' },
  mod:     { r: 3,   tone: 'orange',  label: 'Mod/ROP' },
  gi:      { r: 4,   tone: 'blue',    label: 'GI' },
  decline: { r: 5,   tone: 'red',     label: 'Decline' },
};

// Each entry: { name, verdicts: [c0..c5] } where each verdict is
// { tier, detail }. The detail text shows in the carrier-by-carrier
// breakdown so the agent can read the exact timeframe rule.
const v = (tier, detail) => ({ tier, detail });
const row = (name, ...verdicts) => ({ name, verdicts });

export const FEX_CONDITIONS = [
  row('AIDS / HIV / ARC',
    v('decline','Decline'), v('decline','Decline'), v('decline','Decline'),
    v('decline','Decline (see state app)'), v('decline','Decline'), v('decline','Decline')),
  row('AFib / Irregular heartbeat',
    v('ok','Not asked'), v('ok','Not asked'), v('ok','Not asked'),
    v('ok','Not asked'), v('ok','Not asked'), v('graded','Within 2 yrs - Graded')),
  row('Alcohol / drug abuse',
    v('gi','Within 2 yrs - GI'),
    v('mod','Treated within 2 yrs - Modified'),
    v('mod','Within 2 yrs - Modified'),
    v('mod','Treated within 2 yrs - ROP'),
    v('mod','Treated within 2 yrs - Basic'),
    v('graded','Within 2 yrs - Graded')),
  row("ALS (Lou Gehrig's)",
    v('gi','Guaranteed Issue'), v('decline','Decline'), v('decline','Decline'),
    v('decline','Decline'), v('decline','Decline'), v('decline','Decline')),
  row("Alzheimer's / dementia / cognitive",
    v('gi','Guaranteed Issue'), v('decline','Decline'), v('decline','Decline'),
    v('decline','Decline'), v('decline','Decline'), v('decline','Decline')),
  row('Amputation',
    v('decline','Due to diabetes - Decline'),
    v('mod','Modified; due to disease/diabetes - Decline'),
    v('mod','Due to diabetes - Modified'),
    v('decline','Caused by disease - Decline'),
    v('decline','Decline'),
    v('decline','Due to diabetes - Decline')),
  row('Aneurysm',
    v('ok','Not asked - allowed'),
    v('mod','Within 1 yr - Modified'),
    v('mod','Within 2 yrs - Modified'),
    v('mod','Within 2 yrs - ROP'),
    v('mod','Within 2 yrs - Basic'),
    v('check','Asked - verify')),
  row('Angina (chest pain)',
    v('gi','Within 1 yr - GI'),
    v('mod','Tx within 1 yr - Modified; 1-2 yrs - Standard; >2 yrs - Preferred'),
    v('mod','Within 2 yrs - Modified'),
    v('mod','Within 2 yrs - ROP'),
    v('mod','Tx within 1 yr - Basic; within 2 yrs - Standard'),
    v('decline','Within 1 yr - Decline; within 2 yrs - Graded')),
  row('Angioplasty',
    v('gi','Within 1 yr - GI'),
    v('check','See Heart Surgery'),
    v('mod','Within 2 yrs - Modified'),
    v('ok','Not asked - allowed'),
    v('check','See Heart Surgery'),
    v('decline','Within 1 yr - Decline; within 2 yrs - Graded')),
  row('Arthritis',
    v('ok','Not asked'), v('ok','Not asked'), v('ok','Not asked'),
    v('ok','Not asked'), v('ok','Not asked'), v('ok','Not asked')),
  row('Assisted living / LTC facility',
    v('gi','Within 6 mos - GI'), v('decline','Decline'), v('decline','Current - Decline'),
    v('decline','Decline'), v('decline','Decline'), v('decline','Current - Decline')),
  row('Asthma (chronic)',
    v('ok','Not asked'), v('ok','Standard'), v('ok','Not asked'),
    v('ok','Not asked'), v('ok','Not asked'), v('ok','Not asked')),
  row('Autism',
    v('ok','Not asked'), v('check','See Mental Incapacity'), v('graded','Graded'),
    v('ok','Not asked'), v('ok','Not asked'), v('decline','Decline')),
  row('Bipolar',
    v('ok','Not asked'), v('ok','Preferred'), v('graded','Graded'),
    v('ok','Not asked'), v('ok','Preferred'), v('graded','Within 4 yrs - Graded')),
  row('Black lung',
    v('ok','Not asked'), v('ok','Not asked'), v('graded','Graded'),
    v('ok','Not asked'), v('ok','Not asked'), v('ok','Not asked')),
  row('Blood clots',
    v('check','See Stent / check meds'),
    v('check','Ask re: stents / heart surgery'),
    v('check','See Stent'),
    v('mod','Surgery within 2 yrs - ROP; within 3 yrs - Graded'),
    v('check','See Stent'),
    v('check','See Stent')),
  row('Blood disorder (polycythemia, hemophilia, coagulation)',
    v('ok','Not asked'), v('ok','Not asked'), v('graded','Graded'),
    v('check','See Blood Clotting'), v('ok','Not asked'), v('ok','Not asked')),
  row('Bone marrow transplant',
    v('ok','Not asked'), v('decline','Decline'), v('decline','Decline'),
    v('ok','Not asked'), v('decline','Decline'), v('decline','Decline')),
  row('Bronchitis (chronic)',
    v('check','Check inhalers / meds'),
    v('ok','Standard'),
    v('check','Check inhalers / meds'),
    v('mod','Treated within 2 yrs - ROP; within 3 yrs - Graded'),
    v('check','Check inhalers / meds'),
    v('graded','Graded')),
  row('Cancer (other than basal cell)',
    v('gi','Metastatic/recurrent - Decline; within 2 yrs - GI; >2 yrs - allowed'),
    v('decline','Current/treated within 2 yrs/recurring - Decline; basal/squamous - Preferred'),
    v('mod','Recurring/current - Decline; within 3 yrs - Modified'),
    v('mod','Current - Decline; recurring/within 2 yrs - ROP; within 3 yrs - Graded'),
    v('mod','Current - Decline; within 3 yrs - Basic'),
    v('decline','<2 yrs/metastatic/recurring - Decline; 2-4 yrs - Graded; >4 yrs - allowed')),
  row('Cerebral palsy',
    v('ok','Not asked'), v('decline','Decline'), v('ok','Not asked'),
    v('graded','Within 3 yrs - Graded'), v('ok','Not asked'), v('ok','Not asked')),
  row('Chronic pain (6+ narcotic fills)',
    v('check','Check med list'), v('check','Check meds'), v('check','Check med list'),
    v('check','Check med list - see Neuropathy'), v('check','Check meds'), v('check','Check med list')),
  row('Cirrhosis',
    v('gi','Guaranteed Issue'),
    v('mod','Treated within 2 yrs - Modified'),
    v('graded','Stage C - Decline; Stage A/B - Graded'),
    v('mod','Treated within 2 yrs - ROP; within 3 yrs - Graded'),
    v('ok','Standard'),
    v('decline','Decline')),
  row('Congestive heart failure (CHF)',
    v('gi','Guaranteed Issue'), v('decline','Decline'), v('mod','Within 2 yrs - Modified'),
    v('decline','Decline'), v('decline','Decline'), v('decline','Decline')),
  row('Coronary artery disease',
    v('gi','Within 1 yr - GI'),
    v('check','See Heart Disease'),
    v('check','See Heart Disease'),
    v('mod','ROP'),
    v('check','See Heart Disease'),
    v('decline','Within 1 yr - Decline; within 2 yrs - Graded')),
  row('COPD',
    v('gi','Guaranteed Issue'),
    v('ok','Standard'),
    v('graded','Graded'),
    v('mod','Treated within 2 yrs - ROP; within 3 yrs - Graded; >3 yrs - Immediate'),
    v('ok','Standard'),
    v('graded','Graded')),
  row("Crohn's disease",
    v('ok','Allowed'), v('ok','Not asked'), v('ok','Not asked'),
    v('ok','Not asked'), v('ok','Not asked'), v('ok','Not asked')),
  row('Cystic fibrosis',
    v('gi','Guaranteed'), v('decline','Decline'), v('ok','Not asked'),
    v('ok','Not asked'), v('ok','Not asked'), v('graded','Graded')),
  row('Dementia',
    v('gi','Guaranteed Issue'), v('decline','Decline'), v('decline','Decline'),
    v('decline','Decline'), v('decline','Decline'), v('decline','Decline')),
  row('Depression',
    v('ok','Not asked'), v('ok','Not asked'), v('graded','If bipolar - Graded'),
    v('ok','Not asked'), v('ok','Not asked'), v('graded','Bipolar within 4 yrs - Graded')),
  row('Diabetes',
    v('check','Allowed - see complications'),
    v('check','Allowed - see complications'),
    v('check','Allowed - see complications'),
    v('mod','Insulin prior to age 50 - ROP; else Immediate (see complications)'),
    v('check','Allowed - see complications'),
    v('graded','Dx/treated prior to age 50 - Graded (see complications)')),
  row('Diabetic complications (insulin shock, coma, retinopathy, nephropathy, neuropathy)',
    v('gi','Within 2 yrs - GI'),
    v('mod','Amputation - Decline; coma/shock/neuropathy/nephropathy/retinopathy - Modified'),
    v('mod','Insulin shock/coma/amputation - Modified; others - Preferred'),
    v('mod','Dx/treated for complications - ROP'),
    v('mod','Insulin shock/coma/amputation - Decline; neuropathy/PVD/PAD/retinopathy - Basic'),
    v('graded','Insulin shock/coma - Decline; retinopathy/nephropathy/neuropathy - Graded')),
  row('Dialysis',
    v('gi','Kidney dialysis - GI'),
    v('decline','Within 1 yr - Decline'),
    v('decline','Decline'),
    v('decline','Decline'),
    v('decline','Within 1 yr - Decline'),
    v('decline','Decline')),
  row("Down's syndrome",
    v('ok','Not asked'), v('check','See Mental Incapacity'), v('graded','Graded'),
    v('check','See Mental Incapacity'), v('ok','Not asked'), v('decline','Decline')),
  row('DUI',
    v('ok','Not asked'),
    v('mod','Within 2 yrs - Modified'),
    v('mod','Within 2 yrs - Modified'),
    v('ok','Not asked'),
    v('check','Within 2 years'),
    v('ok','Not asked')),
  row('Emphysema (chronic)',
    v('gi','Guaranteed Issue'),
    v('ok','Standard'),
    v('graded','Graded'),
    v('mod','Treated within 2 yrs - ROP; within 3 yrs - Graded'),
    v('ok','Standard'),
    v('graded','Graded')),
  row('Epilepsy / seizures',
    v('ok','Not asked'), v('ok','Not asked'), v('ok','Not asked'),
    v('graded','Within 3 yrs - Graded'), v('ok','Not asked'), v('ok','Not asked')),
  row('Felony',
    v('gi','Within 6 mos - GI'), v('ok','Not asked'), v('ok','Not asked'),
    v('ok','Not asked'), v('ok','Not asked'),
    v('graded','Convicted within 2 yrs / awaiting trial - Graded')),
  row('Heart attack',
    v('gi','Within 1 yr - GI'),
    v('mod','Within 1 yr - Modified; 1-2 yrs - Standard; >2 yrs - Preferred'),
    v('mod','Within 2 yrs - Modified'),
    v('mod','Within 2 yrs - ROP; within 3 yrs - Graded'),
    v('mod','Within 1 yr - Basic; within 2 yrs - Standard'),
    v('decline','Within 1 yr - Decline; within 2 yrs - Graded')),
  row('Heart surgery',
    v('gi','Within 1 yr - GI'),
    v('mod','Within 1 yr - Modified; 1-2 yrs - Standard; >2 yrs - Preferred'),
    v('mod','Within 2 yrs - Modified'),
    v('mod','Within 2 yrs - ROP; within 3 yrs - Graded'),
    v('mod','Within 1 yr - Basic; within 2 yrs - Standard'),
    v('decline','Within 1 yr - Decline; within 2 yrs - Graded')),
  row('Heart valve replacement',
    v('gi','Within 1 yr - GI'),
    v('mod','Within 1 yr - Modified; 1-2 yrs - Standard; >2 yrs - Preferred'),
    v('mod','Within 2 yrs - Modified'),
    v('mod','Within 2 yrs - ROP; within 3 yrs - Graded'),
    v('mod','Within 1 yr - Basic; within 2 yrs - Standard'),
    v('decline','Within 1 yr - Decline; within 2 yrs - Graded')),
  row('Hepatitis A',
    v('ok','Allowed'),
    v('mod','Within 2 yrs - Modified'),
    v('graded','Chronic/current - Graded'),
    v('mod','Chronic/treated within 2 yrs - ROP'),
    v('ok','Not asked'),
    v('ok','Not asked')),
  row('Hepatitis B',
    v('gi','Guaranteed Issue'),
    v('mod','Within 2 yrs - Modified'),
    v('graded','Chronic/current - Graded'),
    v('mod','Chronic/treated within 2 yrs - ROP'),
    v('ok','Standard'),
    v('ok','Not asked')),
  row('Hepatitis C',
    v('gi','Guaranteed Issue'),
    v('mod','Within 2 yrs - Modified'),
    v('graded','Chronic/current - Graded'),
    v('mod','Chronic/treated within 2 yrs - ROP; within 3 yrs - Graded'),
    v('ok','Standard'),
    v('graded','Graded')),
  row("Huntington's disease",
    v('ok','Not asked'), v('decline','Decline'), v('ok','Not asked'),
    v('ok','Not asked'), v('ok','Not asked'), v('decline','Decline')),
  row('Illegal drugs',
    v('gi','Within 2 yrs - GI'),
    v('mod','Treated within 2 yrs - Modified'),
    v('check','See Felony or DUI'),
    v('mod','Used within 2 yrs - ROP'),
    v('mod','Within 2 yrs - Basic'),
    v('graded','Within 2 yrs - Graded')),
  row('Jail / incarcerated',
    v('decline','Decline'), v('ok','Not asked'), v('ok','Not asked'),
    v('decline','Decline'), v('ok','Not asked'), v('decline','Decline')),
  row('Kidney disease / disorder / failure',
    v('gi','Chronic - GI'),
    v('graded','Graded (also see dialysis)'),
    v('graded','Graded (also see dialysis)'),
    v('mod','Dialysis - Decline; failure/disease - ROP'),
    v('ok','Standard (ask re: dialysis)'),
    v('decline','Decline')),
  row('Liver disease',
    v('gi','Guaranteed Issue'),
    v('graded','Graded'),
    v('graded','Graded'),
    v('decline','Liver failure - Decline; disease within 3 yrs - Graded'),
    v('ok','Standard'),
    v('decline','Cirrhosis - Decline')),
  row('Lupus (SLE)',
    v('ok','Not asked'),
    v('graded','Graded'),
    v('graded','Graded'),
    v('mod','Treated/Dx within 2 yrs - ROP'),
    v('ok','Standard'),
    v('graded','Within 4 yrs + systemic - Graded')),
  row('Melanoma',
    v('decline','Malignant - Decline'),
    v('mod','Within 3 yrs - Modified (excl basal/squamous)'),
    v('mod','Within 3 yrs - Modified (excl basal/squamous)'),
    v('check','See Cancer'),
    v('ok','Basal cell - Preferred; else see Cancer'),
    v('decline','Within 2 yrs - Decline; within 4 yrs - Graded')),
  row('Mental incapacity / retardation',
    v('ok','Not asked'), v('graded','Graded'), v('graded','Graded'),
    v('decline','Decline'), v('ok','Not asked'), v('ok','Not asked')),
  row('Multiple sclerosis (MS)',
    v('ok','Not asked'),
    v('ok','Not asked'),
    v('ok','Not asked'),
    v('graded','Dx/treated within 3 yrs - Graded'),
    v('ok','Preferred'),
    v('graded','Within 4 yrs - Graded')),
  row('Neuropathy',
    v('gi','Due to diabetes/treated within 2 yrs - GI; not diabetes - check med list'),
    v('mod','Due to diabetes - Modified; otherwise ok'),
    v('check','See diabetic complications'),
    v('mod','Due to diabetes prior to age 50 - ROP; not diabetes - Immediate'),
    v('mod','Due to diabetes - Basic; otherwise ok'),
    v('graded','Due to diabetes prior to age 50 - Graded; not diabetes - allowed')),
  row('Oxygen use',
    v('gi','Within 6 mos - GI'),
    v('decline','Within 1 yr - Decline (CPAP ok)'),
    v('decline','Decline'),
    v('decline','Decline'),
    v('decline','Within 1 yr - Decline'),
    v('decline','Decline')),
  row('Pacemaker / defibrillator implant',
    v('gi','Within 1 yr - GI'),
    v('mod','Within 1 yr - Modified; 1-2 yrs - Standard; >2 yrs - Preferred'),
    v('mod','Within 2 yrs - Modified'),
    v('mod','Within 2 yrs - ROP; within 3 yrs - Graded'),
    v('mod','Within 1 yr - Basic; within 2 yrs - Standard; >2 yrs - Preferred'),
    v('check','Check meds / heart conditions - could be Graded')),
  row('Pancreatitis',
    v('ok','Not asked'), v('ok','Not asked'), v('ok','Not asked'),
    v('mod','Chronic/treated within 2 yrs - ROP'),
    v('ok','Not asked'), v('ok','Not asked')),
  row("Parkinson's disease",
    v('ok','Allowed'),
    v('ok','Standard'),
    v('graded','Graded'),
    v('graded','Treated/Dx within 3 yrs - Graded'),
    v('ok','Standard if all ADLs; else Decline'),
    v('graded','Within 4 yrs - Graded')),
  row('Parole / probation (currently)',
    v('gi','Within 6 mos - GI'), v('ok','Not asked'), v('ok','Not asked'),
    v('ok','Not asked'), v('ok','Not asked'),
    v('graded','Convicted/awaiting trial within 2 yrs - Graded')),
  row('PAD / PVD',
    v('ok','Not asked'), v('ok','Not asked'), v('ok','Not asked'),
    v('ok','Not asked'), v('check','See diabetic complication'),
    v('graded','Graded')),
  row('PTSD',
    v('ok','Allowed'), v('ok','Not asked'), v('ok','Not asked'),
    v('ok','Not asked'), v('ok','Not asked'), v('ok','Not asked')),
  row('Pulmonary fibrosis',
    v('check','Ask about oxygen'), v('decline','Decline'),
    v('check','Ask about oxygen'), v('check','Ask about oxygen'),
    v('check','Ask about oxygen'), v('check','Ask about oxygen')),
  row('Rheumatoid arthritis',
    v('ok','Not asked'), v('ok','Not asked'), v('ok','Not asked'),
    v('ok','Not asked'), v('ok','Not asked'), v('ok','Not asked')),
  row('Sarcoidosis',
    v('ok','Not asked'), v('ok','Not asked'), v('ok','Not asked'),
    v('ok','Not asked'), v('ok','Not asked'), v('graded','Graded')),
  row('Schizophrenia',
    v('ok','Allowed'),
    v('ok','Preferred'),
    v('graded','Graded'),
    v('ok','Not asked'),
    v('ok','Preferred'),
    v('graded','Treated/Dx within 4 yrs - Graded')),
  row('Sickle cell anemia',
    v('ok','Allowed'), v('decline','Decline'), v('graded','Graded'),
    v('ok','Not asked'), v('ok','Not asked'), v('decline','Decline')),
  row('Sleep apnea',
    v('ok','Not asked'), v('ok','Not asked'), v('ok','Not asked'),
    v('check','See Oxygen use'), v('ok','Not asked'), v('graded','Graded')),
  row('Stent',
    v('gi','Within 1 yr - GI'),
    v('check','See Heart Surgery'),
    v('mod','Within 2 yrs - Modified'),
    v('mod','Within 2 yrs - ROP; within 3 yrs - Graded'),
    v('mod','Within 1 yr - Basic; within 2 yrs - Standard'),
    v('decline','Within 1 yr - Decline; within 2 yrs - Graded')),
  row('Stroke / TIA',
    v('gi','Within 1 yr - GI; TIA - not asked'),
    v('mod','Within 1 yr - Modified; 1-2 yrs - Standard; >2 yrs - Preferred'),
    v('mod','Within 2 yrs - Modified'),
    v('mod','Stroke/TIA within 2 yrs - ROP; stroke within 3 yrs - Graded'),
    v('mod','Within 1 yr - Basic; within 2 yrs - Standard'),
    v('graded','Within 2 yrs - Graded')),
  row('Terminal illness',
    v('decline','Death within 2 yrs - Decline'),
    v('decline','Decline'),
    v('decline','Decline'),
    v('decline','Death in next 12 mos - Decline'),
    v('decline','Decline'),
    v('decline','Death in next 12 mos - Decline')),
  row('Organ transplant',
    v('gi','Guaranteed Issue'), v('decline','Decline'), v('decline','Decline'),
    v('decline','Decline'), v('decline','Decline'), v('decline','Decline')),
  row('Tuberculosis',
    v('ok','Not asked'), v('ok','Not asked'), v('graded','Graded'),
    v('ok','Not asked'), v('ok','Not asked'), v('ok','Not asked')),
  row('Ulcerative colitis',
    v('ok','Not asked'), v('ok','Not asked'), v('ok','Not asked'),
    v('graded','Within 3 yrs - Graded'), v('ok','Not asked'), v('ok','Not asked')),
  row('Walker',
    v('gi','Within 6 mos - GI'), v('ok','Not asked'), v('ok','Not asked'),
    v('ok','Not asked'), v('ok','Not asked'), v('ok','Not asked')),
  row('Wheelchair / electric scooter',
    v('gi','Within 6 mos - GI'),
    v('decline','Use wheelchair/scooter - Decline'),
    v('decline','Confined or due to diabetes - Decline'),
    v('decline','Due to illness/disease - Decline'),
    v('decline','Confined or due to diabetes - Decline'),
    v('decline','Due to illness/disease - Decline')),
];

// Pure ranking helper. Returns an array sorted best-first with each
// entry: { ci, name, product, worstR, limitingCondition }. Empty
// input returns null.
export function rankFexCarriers(selectedIndices) {
  if (!selectedIndices || selectedIndices.length === 0) return null;
  const ranked = FEX_CARRIERS.map((car, ci) => {
    let worstR = -1;
    let limiting = '';
    for (const i of selectedIndices) {
      const cell = FEX_CONDITIONS[i]?.verdicts?.[ci];
      if (!cell) continue;
      const r = FEX_TIERS[cell.tier]?.r ?? 0;
      if (r > worstR) { worstR = r; limiting = FEX_CONDITIONS[i].name; }
    }
    return { ci, name: car.name, product: car.product, worstR, limitingCondition: limiting };
  }).sort((a, b) => a.worstR - b.worstR);
  return ranked;
}
